// src/components/ui/ConfirmDialog.jsx
import React, { useEffect } from "react";
import Button from "./Button";
import Icon from "./Icon";

/**
 * Generic confirmation modal for destructive, costly, or otherwise
 * sensitive actions. Shared across both the admin dashboard and the
 * regular app (e.g. confirming credit-spend before regenerating picks).
 *
 * Usage:
 *   <ConfirmDialog
 *     open={isOpen}
 *     title="Deactivate user?"
 *     message="They won't be able to log in until reactivated."
 *     confirmLabel="Deactivate"
 *     tone="danger"
 *     onConfirm={async () => { ... }}
 *     onClose={() => setOpen(false)}
 *   />
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",          // "default" | "danger"
  loading = false,
  onConfirm,
  onClose,
  icon,
  children,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const accent =
    tone === "danger"
      ? { color: "var(--red)", bg: "var(--red-dim)", border: "var(--red-border)" }
      : { color: "var(--gold)", bg: "var(--gold-dim)", border: "var(--gold-border)" };

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
      onClick={() => !loading && onClose?.()}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md fade-in-up"
        style={{
          background: "var(--bg)",
          backgroundColor: "var(--bg)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r-xl)",
          padding: 24,
          boxShadow: "var(--shadow-lg)",
          opacity: 1,
        }}
      >
        <div className="flex items-start gap-3 mb-3">
          <div
            className="shrink-0 inline-flex items-center justify-center rounded-xl"
            style={{
              width: 40,
              height: 40,
              background: accent.bg,
              color: accent.color,
              border: `1px solid ${accent.border}`,
            }}
          >
            <Icon name={icon || (tone === "danger" ? "alert-circle" : "shield-check")} size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold mb-1" style={{ fontSize: 16, color: "var(--txt)" }}>
              {title}
            </h3>
            {message && (
              <p className="text-[13px] leading-relaxed" style={{ color: "var(--txt-2)" }}>
                {message}
              </p>
            )}
            {children && (
              <div className="mt-3">{children}</div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "gold"}
            size="sm"
            onClick={onConfirm}
            loading={loading}
            disabled={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}